import { ref, reactive } from 'vue'
import { resolveImageUrl } from '@/utils/imageUrl'

/** 停顿超时：连续该时长无字节进展则判定下载卡死 */
const STALL_TIMEOUT_MS = 60 * 1000

/** 下载任务状态 */
export type DownloadStatus = 'downloading' | 'done' | 'error'

/** 单个下载任务（供进度面板展示） */
export interface DownloadTask {
  id: string
  /** 文件名 */
  name: string
  /** 进度百分比 0-100；-1 表示总大小未知（不确定进度） */
  progress: number
  status: DownloadStatus
  /** 已下载字节 */
  loaded: number
  /** 总字节，0 表示未知 */
  total: number
  /** 失败原因 */
  error?: string
}

/** 全局共享的下载任务列表（模块级单例） */
const tasks = ref<DownloadTask[]>([])
let seq = 0

/** 读取全局下载任务列表 */
export function useDownloadTasks() {
  return { tasks, removeTask }
}

/** 移除某个任务（完成态自动清理 / 用户手动关闭失败项） */
export function removeTask(id: string) {
  const i = tasks.value.findIndex(t => t.id === id)
  if (i >= 0) tasks.value.splice(i, 1)
}

/** 完成/失败后延迟清理完成态任务 */
function scheduleCleanup(id: string, delay = 3000) {
  setTimeout(() => removeTask(id), delay)
}

/** 从 URL 末段推断文件名 */
function guessFilename(url: string): string {
  try {
    const u = new URL(url, window.location.href)
    const seg = u.pathname.split('/').filter(Boolean).pop()
    return seg ? decodeURIComponent(seg) : 'download'
  } catch {
    return 'download'
  }
}

/** 若文件名缺少扩展名，则从 URL 补上（如 分镜图_1 → 分镜图_1.png） */
function ensureExt(name: string, url: string): string {
  if (/\.[a-zA-Z0-9]{1,5}$/.test(name)) return name
  const m = url.match(/\.([a-zA-Z0-9]{1,5})(?:\?|#|$)/)
  return m ? `${name}.${m[1]}` : name
}

/** 把 blob 存为文件 */
function saveBlob(blob: Blob, filename: string) {
  const objUrl = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = objUrl
  a.download = filename
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(objUrl), 1000)
}

/**
 * 跨域无法读流时的降级：用带 target=_blank 的锚点直链。
 * - 不能用 window.open：异步回调里已脱离用户手势，浏览器会弹出 about:blank 空白窗口
 * - 加 target=_blank 保证不导航当前页（跨域下 download 属性可能被忽略）
 */
function fallbackOpen(url: string, filename: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.target = '_blank'
  a.rel = 'noopener'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * 统一文件下载。
 * - 传入相对路径（如 `Character/xxx.png`）或完整 URL 均可；内部统一用 resolveImageUrl 归一
 * - 通过 XHR 拉取 blob，带字节级进度（进度写入全局任务列表，由 DownloadPanel 展示）
 * - blob + a.download 保证跨浏览器一致：强制下载、文件名可控
 * - CORS 受限导致拉流失败时，退化为锚点直链下载（不弹空白窗口），保证「能下」只是「无进度」
 *
 * @param path 资源路径或完整 URL
 * @param filename 可选自定义文件名（优先级最高）
 */
export function downloadFile(path: string, filename?: string): Promise<void> {
  const url = resolveImageUrl(path)
  if (!url) return Promise.reject(new Error('无效的下载地址'))

  const id = `dl-${Date.now()}-${seq++}`
  const initialName = filename ? ensureExt(filename, url) : guessFilename(url)
  // 用 reactive 创建：后续直接改 task.xxx 即可触发更新。
  // 若用普通对象 push 进数组，代码里持有的是原始对象，改它不会触发响应式。
  const task = reactive<DownloadTask>({
    id,
    name: initialName,
    progress: 0,
    status: 'downloading',
    loaded: 0,
    total: 0,
  })
  tasks.value.push(task)

  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('GET', url, true)
    xhr.responseType = 'blob'

    // 停顿检测：只要字节仍在传输就不算超时，连续 STALL_TIMEOUT_MS 无进展才判定卡死。
    // 避免对「大文件 + 慢网络」误判超时，又能兜住连接假死。
    let stallTimer: ReturnType<typeof setTimeout> | null = null
    let stalled = false
    const clearStall = () => { if (stallTimer) { clearTimeout(stallTimer); stallTimer = null } }
    const resetStall = () => {
      clearStall()
      stallTimer = setTimeout(() => {
        stalled = true
        xhr.abort()
      }, STALL_TIMEOUT_MS)
    }

    xhr.onprogress = (e) => {
      resetStall()
      if (e.lengthComputable && e.total > 0) {
        task.progress = Math.min(100, Math.round((e.loaded / e.total) * 100))
        task.total = e.total
      } else {
        task.progress = -1
      }
      task.loaded = e.loaded
    }

    xhr.onload = () => {
      clearStall()
      if (xhr.status >= 200 && xhr.status < 300) {
        task.name = initialName
        task.progress = 100
        task.status = 'done'
        saveBlob(xhr.response as Blob, initialName)
        scheduleCleanup(id)
        resolve()
      } else {
        task.status = 'error'
        task.error = `下载失败 (${xhr.status})`
        reject(new Error(task.error))
      }
    }

    xhr.onerror = () => {
      clearStall()
      // 无法读流（多为对象存储未放行跨域）：退化为锚点直链下载。
      // 不能用 window.open —— 该调用发生在异步回调里，已脱离用户点击手势，
      // 浏览器会弹出 about:blank 空白窗口。锚点直链则不会产生空白窗口。
      task.name = initialName
      task.status = 'done'
      task.progress = 100
      fallbackOpen(url, initialName)
      scheduleCleanup(id)
      resolve()
    }

    xhr.onabort = () => {
      clearStall()
      task.status = 'error'
      task.error = stalled ? '下载超时，请重试' : '已取消'
      reject(new Error(task.error))
    }

    resetStall()
    xhr.send()
  })
}
